import Phaser from "phaser";
import { explosionRing, floatingText } from "@yjsgames/phaser-fx";
import { COMBAT, REGEN_BRICK } from "../data/balance.js";
import { needsSweep, sweepHitsAabb } from "../objects/projectileSweep.js";
import { planRegenHeals } from "../systems/regenHeal.js";
import { hexToTint } from "../objects/brickAppearance.js";
import { formatBig } from "../ui/formatNumber.js";
import { FONT_FAMILY } from "../ui/uiTokens.js";
import { Brick } from "../objects/Brick.js";
import { Projectile } from "../objects/Projectile.js";
import { diag } from "../app/diag.js";
import type { BattlefieldRuntimePools } from "./battlefieldRuntimePools.js";

const specialLog = diag("specialBrick");

export interface BattlefieldFrameRuntimeDeps {
  readonly scene: Phaser.Scene;
  readonly runtime: BattlefieldRuntimePools | undefined;
  readonly paused: boolean;
  readonly handleHit: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback;
  readonly updateTargetMarker: () => void;
}

export function updateBattlefieldFrame(deps: BattlefieldFrameRuntimeDeps): void {
  const runtime = deps.runtime;
  if (!runtime || deps.paused) return;
  const nowMs = deps.scene.time.now;
  sweepProjectiles(deps.scene, runtime, deps.handleHit);
  if (runtime.shouldScanRegen(nowMs)) updateRegenBricks(deps.scene, runtime, nowMs);
  deps.updateTargetMarker();
}

function sweepProjectiles(
  scene: Phaser.Scene,
  runtime: BattlefieldRuntimePools,
  handleHit: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
): void {
  const threshold = COMBAT.ballRadius * 2;
  let bricks: Brick[] | undefined;
  for (const obj of runtime.projectiles.getChildren()) {
    const proj = obj as Projectile;
    if (!proj.active) continue;
    const prev = proj.sweepPrev();
    if (needsSweep(prev.x, prev.y, proj.x, proj.y, threshold)) {
      bricks ??= runtime.bricks.getChildren() as Brick[];
      for (const brick of bricks) {
        if (!brick.active || proj.hitSet.has(brick)) continue;
        const hit = sweepHitsAabb(
          { fromX: prev.x, fromY: prev.y, toX: proj.x, toY: proj.y, radius: COMBAT.ballRadius },
          { cx: brick.x, cy: brick.y, halfW: brick.displayWidth / 2, halfH: brick.displayHeight / 2 },
        );
        if (!hit) continue;
        handleHit(
          proj as unknown as Phaser.Types.Physics.Arcade.GameObjectWithBody,
          brick as unknown as Phaser.Types.Physics.Arcade.GameObjectWithBody,
        );
        if (!proj.active) break;
      }
    }
    proj.commitSweepPos();
  }
  void scene;
}

function updateRegenBricks(
  scene: Phaser.Scene,
  runtime: BattlefieldRuntimePools,
  nowMs: number,
): void {
  const targets = (runtime.bricks.getChildren() as Brick[]).filter((b) => b.active);
  const regens = targets.filter((b) => b.kind === "regen" && b.healIntervalMs > 0);
  if (regens.length === 0) return;
  const plans = planRegenHeals(
    regens.map((r) => ({
      gridX: r.gridX,
      gridY: r.gridY,
      healRadius: r.healRadius,
      healAmountRatio: r.healAmountRatio,
      lastHealMs: r.lastHealMs,
      healIntervalMs: r.healIntervalMs,
    })),
    targets.map((t) => ({ gridX: t.gridX, gridY: t.gridY, hp: t.hp, maxHp: t.maxHp })),
    nowMs,
  );
  for (const plan of plans) {
    const regen = regens[plan.regenIndex]!;
    regen.lastHealMs = nowMs;
    if (plan.heals.length === 0) continue;
    const radius = Math.max(regen.displayWidth, regen.displayHeight) * 0.9;
    runtime.maskFx(
      explosionRing(scene, regen.x, regen.y, radius, {
        color: hexToTint(REGEN_BRICK.tint),
        alpha: 0.4,
        endScale: 1.8,
        duration: 360,
      }),
    );
    for (const h of plan.heals) {
      const t = targets[h.targetIndex]!;
      t.applyHeal(h.newHp);
      runtime.maskFx(
        floatingText(scene, {
          x: t.x,
          y: t.y - 6,
          text: `+${formatBig(h.amount)}`,
          color: REGEN_BRICK.tint,
          fontFamily: FONT_FAMILY,
          pool: runtime.fxTextPool,
        }),
      );
    }
    specialLog.diag("regen", `heal ${plan.heals.length} @${regen.gridX},${regen.gridY}`);
  }
}
